import { ApiError, fieldErrors } from "../api/client";

/**
 * UX3010-05 (30.10.2026): what the card says when a save is refused. An error of input goes next to its field as
 * «что не так: как исправить» (the server words it — `errors[].message` of a 422, the `detail` of a check that names
 * its field); a failure of the system (5xx, a broken answer, no network) is never dressed up as a wrong value: it says
 * so, keeps the form as typed and gives the reference code of the request for support.
 */
export type ProblemKind = "input" | "system" | "network" | "denied" | "other" | "auth" | "not_found" | "conflict";

export interface FieldProblem {
  field: string;
  message: string;
}

export interface FormProblem {
  kind: ProblemKind;
  /** Errors bound to a field of the form. */
  fields: FieldProblem[];
  /** Errors of lines (the line number is known, the field of the card is not) and other unbound ones. */
  other: string[];
  /** One sentence for the toast and the block above the buttons. */
  summary: string;
  /** Reference code of the failed request («Код обращения»), for a system error. */
  reference: string | null;
}

const LOC_ROOTS = new Set(["body", "query", "path"]);

interface RawEntry {
  loc?: unknown[];
  msg?: string;
  message?: string;
  field?: string | null;
  line_no?: number | null;
  /** UXW-57: the product of the line, when the server names it («CUP-01 — Чашка»). */
  product?: string | null;
  object?: string | null;
}

/** UXW-57: the words of the common fields of a document line — never «Поле «location_id»». */
const LINE_WORDS: Record<string, string> = {
  quantity: "Количество",
  quantity_plan: "Количество по плану",
  quantity_fact: "Количество факт",
  location_id: "Ячейка",
  batch_id: "Партия",
  storage_object_id: "Товар",
  package_id: "Упаковка",
  packages_count: "Количество упаковок",
  line_id: "Строка документа",
};

function sentence(text: string): string {
  const t = text.trim();
  return t ? t[0].toUpperCase() + t.slice(1) : t;
}

/** The client's short translation of a raw pydantic `msg` («не длиннее 64 символов») — for an older server. */
function translated(problem: ApiError["problem"], entry: RawEntry): string {
  const one = fieldErrors(new ApiError({ title: problem.title, status: problem.status, errors: [entry] }))[0];
  return sentence(one?.message ?? entry.msg ?? "Неверное значение");
}

function referenceOf(problem: ApiError["problem"]): string | null {
  const raw = (problem as unknown as Record<string, unknown>).reference;
  if (typeof raw === "string" && raw && raw !== "-") return raw;
  const m = /Код обращения:\s*([^\s.;,]+)/.exec(problem.detail ?? "");
  return m ? m[1] : null;
}

export function labelOf(field: string, labels?: Record<string, string>): string {
  return labels?.[field] ?? LINE_WORDS[field] ?? field;
}

/** «Проверьте 1 поле», «Проверьте 2 поля», «Проверьте 5 полей» (UXW-57, ER01). */
export function checkWords(n: number): string {
  const tail = n % 10;
  const tail2 = n % 100;
  const word = tail === 1 && tail2 !== 11 ? "поле" : tail >= 2 && tail <= 4 && (tail2 < 12 || tail2 > 14) ? "поля" : "полей";
  return `Проверьте ${n} ${word}`;
}

/**
 * UXW-59: the answer to a POST is lost — no network, a proxy timeout (502/503/504), a broken page instead of JSON. The
 * server may have done the work: the caller checks the record before offering anything again. A 500 of the WMS
 * itself is a known refusal (its transaction is rolled back) and a failed refresh of the session never sent the request.
 */
export function outcomeUnknown(error: unknown): boolean {
  if (error instanceof ApiError) {
    const { status, code } = error.problem;
    if (code === "unavailable") return false;
    return status === 0 || status === 502 || status === 503 || status === 504;
  }
  return error instanceof SyntaxError;
}

export function explainProblem(error: unknown, labels?: Record<string, string>): FormProblem {
  if (!(error instanceof ApiError)) {
    // a broken answer (an HTML page of a proxy instead of JSON) or a bug of the page itself — not the user's input
    return {
      kind: "system",
      fields: [],
      other: [],
      summary: "Не удалось сохранить из-за сбоя системы, а не из-за ошибки в заполнении. Данные остались в форме — повторите сохранение позже.",
      reference: null,
    };
  }
  const problem = error.problem;
  if (problem.status === 0) {
    return { kind: "network", fields: [], other: [], summary: problem.detail ?? "Нет связи с сервером: проверьте сеть и повторите. Данные остались в форме.", reference: null };
  }
  if (problem.status >= 500) {
    const reference = referenceOf(problem);
    return {
      kind: "system",
      fields: [],
      other: [],
      summary:
        "Не удалось сохранить из-за ошибки системы — это не ошибка в заполнении формы. Данные остались в форме; повторите позже" +
        (reference ? `. Если повторится, сообщите в поддержку код обращения ${reference}.` : "."),
      reference,
    };
  }
  // ER02: a refusal of rights, an ended session, a record that is gone and a changed record are told apart
  if (problem.status === 401) {
    return { kind: "auth", fields: [], other: [], summary: problem.detail ?? "Сеанс завершён: войдите снова. Данные этой формы до входа не сохранены.", reference: null };
  }
  if (problem.status === 403) {
    return { kind: "denied", fields: [], other: [], summary: problem.detail ?? problem.title, reference: null };
  }
  if (problem.status === 404 && !Array.isArray(problem.errors) && !problem.field) {
    return {
      kind: "not_found",
      fields: [],
      other: [],
      summary: `${problem.detail ?? problem.title}. Запись удалена или недоступна вашей роли: откройте список и выберите актуальную.`,
      reference: null,
    };
  }

  const fields: FieldProblem[] = [];
  const other: string[] = [];
  if (Array.isArray(problem.errors)) {
    for (const raw of problem.errors as RawEntry[]) {
      const loc = (raw.loc ?? []).filter((part) => !LOC_ROOTS.has(String(part)));
      // UXW-57: «lines», 2, «quantity» of a request body is the line 3, not a field «lines» of the card
      const lineOfLoc = loc.length >= 2 && typeof loc[1] === "number" ? loc[1] + 1 : null;
      const lineNo = raw.line_no ?? lineOfLoc;
      const field = typeof raw.field === "string" && raw.field ? raw.field : lineOfLoc !== null ? String(loc[2] ?? "") : loc.length ? String(loc[0]) : "";
      const message = raw.message ? sentence(raw.message) : raw.loc ? translated(problem, { ...raw, loc: lineOfLoc !== null ? [field] : raw.loc }) : sentence(raw.msg ?? "");
      if (lineNo !== undefined && lineNo !== null) {
        // a line of the document: the card has no field for it, the text names the line and its product
        const product = raw.product ?? raw.object ?? null;
        other.push(`Строка ${lineNo}${product ? ` (${product})` : ""}${field ? `, «${labelOf(field, labels)}»` : ""}: ${message}`);
      } else if (field) {
        fields.push({ field, message });
      } else if (message) {
        other.push(message);
      }
    }
  }
  if (typeof problem.field === "string" && problem.field && !fields.some((f) => f.field === problem.field)) {
    fields.push({ field: problem.field, message: sentence(problem.detail ?? "Значение уже используется — укажите другое") });
  }
  // a check of the server often names the field itself («Контрагент»: …) — the label is not said twice
  const parts = [
    ...fields.map((f) => (f.message.startsWith(`«${labelOf(f.field, labels)}»`) ? f.message : `«${labelOf(f.field, labels)}»: ${f.message}`)),
    ...other,
  ];
  if (problem.status === 409 && !fields.length && !other.length) {
    // a version or a status changed under the form: not a wrong value — read the record again
    return {
      kind: "conflict",
      fields,
      other,
      summary: `${problem.detail ?? problem.title}. Обновите карточку, проверьте изменения и повторите — введённое остаётся в форме.`,
      reference: null,
    };
  }
  return {
    kind: fields.length || other.length || problem.status === 422 || problem.status === 409 ? "input" : "other",
    fields,
    other,
    summary: parts.length ? parts.join("; ") : problem.detail ?? problem.title,
    reference: null,
  };
}
